import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router';
import { AuthProvider, useAuth } from './components/auth/AuthContext';
import apiClient from './utils/axios';
import { groupName as resolveGroupName, GROUP_CODES } from './config/groups';

// 引入廣告管理器元件
import AdManager from './components/ads/AdManager';

// 引入頁面組件
import HomePage from './components/pages/HomePage';
import GroupPage from './components/pages/GroupPage';
import MessagePage from './components/pages/MessagePage';
import CallbackPage from './components/pages/CallbackPage';
import CompleteProfilePage from './components/pages/CompleteProfilePage';
import PendingApprovalPage from './components/pages/PendingApprovalPage';
import SubscriptionExpiredPage from './components/pages/SubscriptionExpiredPage';
import LoginRequiredPage from './components/pages/LoginRequiredPage'; // 新增登入提示頁面
import ProfilePage from './components/pages/user/ProfilePage';
import AppNavbar from './components/layout/Navbar';
import Forbidden from './components/pages/Forbidden';
import StatsPage from './components/pages/stats/StatsPage'; // 引入統計頁面組件
import YearInReviewPage from './components/pages/yearInReview/YearInReviewPage'; // 引入年度回顧組件
import MaintenancePage from './components/pages/MaintenancePage'; // 引入維護公告頁面
import DownloadPage from './components/pages/DownloadPage';
import IOSAppPage from './components/pages/IOSAppPage';
import PaymentPage from './components/pages/PaymentPage';

// 引入管理員後台組件
import AdminDashboard from './components/pages/admin/AdminDashboard';
import UserManagement from './components/pages/admin/UserManagement';
import AccessManagement from './components/pages/admin/AccessManagement';
import SubscriptionManagement from './components/pages/admin/SubscriptionManagement';
import NotFound from './components/pages/NotFound';

// 引入樣式
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

import { useFCM } from './hooks/useFCM';
import { trackPageView } from './utils/analytics';

// 受保護路由組件
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <div className="text-center mt-5">載入中...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// 管理員路由組件
const AdminRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return <div className="text-center mt-5">載入中...</div>;
  }

  if (!isAuthenticated || !user?.isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// 動態處理 group 路由參數的組件
function DynamicGroupPage() {
  // 使用 React Router 的 useParams 來獲取路由參數
  const { groupCode } = useParams();

  // 從共用團體設定取得中文組名（未知則顯示「未知組別」）
  const defaultGroupName = resolveGroupName(groupCode) || '未知組別';

  // 渲染 GroupPage 組件，傳入解析的 groupCode 和預設組名
  return <GroupPage groupName={defaultGroupName} groupCode={groupCode} />;
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

function AppContent() {
  useFCM();
  const { isAuthenticated, user, loading } = useAuth();
  const [maintenanceInfo, setMaintenanceInfo] = useState(null);
  const [checkingMaintenance, setCheckingMaintenance] = useState(true);
  const location = useLocation();

  // 檢查維護公告
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const response = await apiClient.get('/notice');
        setMaintenanceInfo(response.data);
      } catch (error) {
        console.error('檢查維護公告失敗:', error);
      } finally {
        setCheckingMaintenance(false);
      }
    };

    checkMaintenance();
  }, []);

  // GA page_view：每次路由變動手動送一次
  useEffect(() => {
    trackPageView(location);
  }, [location.pathname, location.search]);

  if (loading || checkingMaintenance) {
    return <div className="text-center mt-5">載入中...</div>;
  }

  // 如果正在維護，只顯示維護公告頁面
  if (maintenanceInfo?.maintenance) {
    return (
      <MaintenancePage
        title={maintenanceInfo.title}
        content={maintenanceInfo.content}
      />
    );
  }

  const isYearInReview = location.pathname.startsWith('/year-in-review');

  return (
    <>
      {!isYearInReview && (
        <AppNavbar
          isAuthenticated={isAuthenticated}
          username={user?.username || ''}
          userRole={user?.role || ''}
        />
      )}
      {/* 加入廣告管理器元件 */}
      {!isYearInReview && <AdManager />}
      <div className="w-100 h-100">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/callback" element={<CallbackPage />} />
          <Route path="/complete-profile" element={<CompleteProfilePage />} />
          <Route path="/pending" element={<PendingApprovalPage />} />
          <Route path="/forbidden" element={<Forbidden />} />
          <Route path="/login-required" element={<LoginRequiredPage />} /> {/* 新增登入提示頁面路由 */}

          {/* 使用實際的訂閱過期頁面元件 */}
          <Route path="/expired" element={<SubscriptionExpiredPage />} />

          <Route path="/profile" element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } />
          {/* 各團短網址（/nogizaka、/equal_love …），由團體設定動態產生 */}
          {GROUP_CODES.map((code) => (
            <Route
              key={code}
              path={`/${code}`}
              element={<GroupPage groupName={resolveGroupName(code)} groupCode={code} />}
            />
          ))}
          <Route path="/groups/:groupCode" element={<DynamicGroupPage />} />
          <Route path="/members/:memberId" element={<MessagePage />} />
          <Route path="/download" element={<DownloadPage />} />
          <Route path="/ios-app" element={<IOSAppPage />} />
          <Route path="/payment" element={<PaymentPage />} />
          <Route path="/stats" element={<StatsPage />} /> {/* 新增統計頁面路由 */}
          <Route path="/year-in-review" element={<YearInReviewPage />} /> {/* 年度回顧路由 */}
          <Route path="/admin" element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }>
            <Route index element={<UserManagement />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="access" element={<AccessManagement />} />
            <Route path="subscriptions" element={<SubscriptionManagement />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
    </>
  );
}

export default App;
